// [after-review-inline P2b] A tiny external store carrying "which clause the
// inline review is on", so the chat thread (writer) can drive the rail document
// (reader) to scroll to + outline that clause. The two live in sibling subtrees
// under Home, so a module store read via useSyncExternalStore is simpler and
// less invasive than threading a context provider through the layout.
/** The clause the rail should scroll to + outline. `seq` is a monotonic bump so an
 *  explicit re-request (Fix #4 — clicking "In the draft" on the CURRENT clause)
 *  forces the rail to re-scroll even though the clause identity is unchanged. */
export type ReviewFocus = {
  /** The draft the review runs on — the rail only reacts when it matches. */
  draftId: string;
  /** The clause name (heading) — used as a fallback locator. */
  name: string;
  /** The clause's draft text — the primary locator (most specific). */
  text: string;
  seq: number;
} | null;

/** What callers pass — `seq` is assigned internally. */
type FocusInput = { draftId: string; name: string; text: string } | null;

let focus: ReviewFocus = null;
let seq = 0;
const subscribers = new Set<() => void>();

function notify(): void {
  for (const fn of subscribers) fn();
}

export function setReviewFocus(next: FocusInput): void {
  if (next === null) {
    if (focus === null) return;
    focus = null;
    notify();
    return;
  }
  // Same clause already focused → keep it (and its seq); no scroll re-trigger.
  if (
    focus !== null &&
    focus.draftId === next.draftId &&
    focus.name === next.name &&
    focus.text === next.text
  ) {
    return;
  }
  seq += 1;
  focus = { ...next, seq };
  notify();
}

/** [Fix #4] Imperatively (re)scroll the rail to a clause — ALWAYS bumps `seq`, so
 *  clicking "In the draft" on the clause already in view still re-scrolls to it. */
export function scrollToClauseNow(input: {
  draftId: string;
  name: string;
  text: string;
}): void {
  seq += 1;
  focus = { ...input, seq };
  notify();
}

export function subscribeReviewFocus(fn: () => void): () => void {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}

export function getReviewFocus(): ReviewFocus {
  return focus;
}
